import React, {
  useCallback,
  useContext,
  createContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { useDataCache } from '../../../../../../contexts/BreakdownDataCacheContext';
import {
  SceneWithUserNames,
  SceneInteriorExterior,
  SceneDayNight,
  UpdateSceneParams,
} from '../../../../../../types/scene.types';
import { ElementSceneInfo, ElementType } from '../../../../../../types/element.types';
import { SlotInfo, CalDateInfo, SlotStatus, SlotType } from '../../../../../../types/schedule.types';
import { sortScenes } from '../../../../../Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { enrichElementRelations } from '../../../../../../utils/enrichElementRelations';
import { sortCastItemsByNO } from '../../../../../../utils/elementSortUtils';
import { useSceneUpdates, useSlotUpdates, useCalDateUpdates } from '../../../../../../hooks/dataUpdates';
import { useRestrictedDatesCast } from '../../../../../../hooks/useRestrictedDatesCast';
import { formatDateFromDate } from '../../../../../../utils/dateUtils';
import { normalizeToSortedIds, mergeSortIds, buildDisplaySegments } from '../../../../../../utils/calDateSlotsSortUtils';
import { syncNoteTextareaHeightToContent } from '../../../../../../utils/noteTextareaAutosize';
import { DropdownMenuItem } from '../../../../../../components/DropdownMenu/DropdownMenu';
import DropdownMenu from '../../../../../../components/DropdownMenu/DropdownMenu';
import { getSceneCodeCellBackgroundColor } from '../../../../../Breakdown/ScenesTable/Utils/SceneCellUtils';
import { ReactComponent as EditIcon } from '../../../../../../assets/svg/Schedule/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../../../../assets/svg/Schedule/delete.svg';
import { ReactComponent as AddIcon } from '../../../../../../assets/svg/Schedule/add.svg';
import { ReactComponent as NoteIcon } from '../../../../../../assets/svg/Schedule/note-solid.svg';
import EditScene, { EDIT_SCENE_PANEL_DRAWER_TOP_PX } from '../../../EditScene';
import './sceneCards.css';


export type ScenesListTabType = 'all' | 'unscheduled' | 'scheduled';

export type CurrentDateDetailRow =
  | { kind: 'scene'; scene: SceneWithUserNames }
  | { kind: 'note'; slot: SlotInfo };

export type ScheduleSceneRowsSortKey = 'sceneCode' | 'sceneSet' | 'sceneLocation' | 'scenePeriod';

export interface ScheduleSceneRowsContextValue {
  canEdit: boolean;
  breakdownID?: string;
  scheduleID?: string;
  sortedScenes: SceneWithUserNames[];
  elementRelations: ElementSceneInfo[];
  todaySceneIds: Set<string>;
  restrictedDatesCastIdsForToday: Set<string>;
  currentDateDetailRows: CurrentDateDetailRow[];
  scheduledSceneIds: Set<string>;
  effectiveScenesListTab: ScenesListTabType;

  editingFields: { [sceneID: string]: { [field: string]: string } };
  setEditingFields: React.Dispatch<
    React.SetStateAction<{ [sceneID: string]: { [field: string]: string } }>
  >;
  pagesNumberInputValues: { [sceneID: string]: string | undefined };
  setPagesNumberInputValues: React.Dispatch<React.SetStateAction<{ [sceneID: string]: string | undefined }>>;
  interiorExteriorMenuVisible: { [sceneID: string]: boolean };
  setInteriorExteriorMenuVisible: React.Dispatch<React.SetStateAction<{ [sceneID: string]: boolean }>>;
  dayNightMenuVisible: { [sceneID: string]: boolean };
  setDayNightMenuVisible: React.Dispatch<React.SetStateAction<{ [sceneID: string]: boolean }>>;
  interiorExteriorMenuPosition: { [sceneID: string]: { top: number; left: number; width: number } };
  setInteriorExteriorMenuPosition: React.Dispatch<
    React.SetStateAction<{ [sceneID: string]: { top: number; left: number; width: number } }>
  >;
  dayNightMenuPosition: { [sceneID: string]: { top: number; left: number; width: number } };
  setDayNightMenuPosition: React.Dispatch<
    React.SetStateAction<{ [sceneID: string]: { top: number; left: number; width: number } }>
  >;
  interiorExteriorButtonRefs: React.MutableRefObject<{ [sceneID: string]: HTMLDivElement | null }>;
  dayNightButtonRefs: React.MutableRefObject<{ [sceneID: string]: HTMLDivElement | null }>;
  interiorExteriorOptions: DropdownMenuItem[];
  dayNightOptions: DropdownMenuItem[];

  cachedScenes: SceneWithUserNames[];
  setCachedScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>;

  editingNoteSlotID: string | null;
  setEditingNoteSlotID: React.Dispatch<React.SetStateAction<string | null>>;

  setEditingSceneId: React.Dispatch<React.SetStateAction<string | null>>;

  getCastRelations: (sceneID: string) => ElementSceneInfo[];
  getCastRelationsForDisplay: (sceneID: string) => ElementSceneInfo[];
  formatInteriorExterior: (value?: import('../../../../../../types/scene.types').SceneInteriorExterior) => string;
  formatDayNight: (value?: import('../../../../../../types/scene.types').SceneDayNight) => string;
  validatePagesNumber: (input: string) => boolean;
  formatPagesNumber: (input: string) => number | undefined;
  handleInteriorExteriorClick: (e: React.MouseEvent, sceneID: string, draggable: boolean) => void;
  handleDayNightClick: (e: React.MouseEvent, sceneID: string, draggable: boolean) => void;
  handleInteriorExteriorSelect: (
    sceneID: string,
    item: DropdownMenuItem,
    breakdownID: string,
    cachedScenes: SceneWithUserNames[],
    setCachedScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => void;
  handleDayNightSelect: (
    sceneID: string,
    item: DropdownMenuItem,
    breakdownID: string,
    cachedScenes: SceneWithUserNames[],
    setCachedScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => void;
  handleFieldUpdate: (
    sceneID: string,
    field: keyof import('../../../../../../types/scene.types').UpdateSceneParams,
    value: any,
    breakdownID: string,
    cachedScenes: SceneWithUserNames[],
    setCachedScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => void;
  handleAddSlot: (e: React.MouseEvent, sceneID: string) => void;
  handleDeleteSlot: (e: React.MouseEvent, sceneID: string) => void;
  handleDeleteNoteSlot: (e: React.MouseEvent, slotID: string) => void;
  handleFinishEditTodayNote: (slotID: string, content: string) => Promise<void>;
}

const ScheduleSceneRowsContext = createContext<ScheduleSceneRowsContextValue | null>(null);

export function useScheduleSceneRows(): ScheduleSceneRowsContextValue {
  const v = useContext(ScheduleSceneRowsContext);
  if (!v) {
    throw new Error('useScheduleSceneRows must be used within ScheduleSceneRowsProvider');
  }
  return v;
}

export { ScheduleSceneRowsContext };

export interface DraggableSceneHeaderProps {
  scene: SceneWithUserNames;
  scheduleID?: string;
  children: React.ReactNode;
  draggable: boolean;
}

export const DraggableSceneHeader: React.FC<DraggableSceneHeaderProps> = ({
  scene,
  scheduleID,
  children,
  draggable,
}) => {
  const draggableId = `scene-${scene.sceneID}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: draggableId,
    data: {
      type: 'scene',
      scene: scene,
      scheduleID: scheduleID,
    },
    disabled: !draggable,
  });

  const childrenWithDragging = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement, {
        className: `${(children as React.ReactElement).props.className || ''} ${isDragging ? 'dragging' : ''}`.trim(),
      })
    : children;

  return (
    <div
      ref={setNodeRef}
      {...(draggable ? listeners : {})}
      {...(draggable ? attributes : {})}
      className="draggable-header-wrapper"
    >
      {childrenWithDragging}
    </div>
  );
};

export type SlotSceneCardVariant = 'todayTab' | 'breakdownTab';

export interface SlotSceneCardProps {
  scene: SceneWithUserNames;
  variant: SlotSceneCardVariant;
  /** 仅 variant === breakdownTab 时用于 scheduled 样式 */
  scenesListTabFilter: ScenesListTabType;
  draggable: boolean;
  editable: boolean;
  showRestrictedDatesStyle: boolean;
}

export const SlotSceneCard: React.FC<SlotSceneCardProps> = ({
  scene,
  variant,
  scenesListTabFilter,
  draggable: draggableProp,
  editable: editableProp,
  showRestrictedDatesStyle,
}) => {
  const { t } = useTranslation();
  const ctx = useScheduleSceneRows();
  const {
    canEdit,
    breakdownID,
    scheduleID,
    todaySceneIds,
    scheduledSceneIds,
    restrictedDatesCastIdsForToday,
    editingFields,
    setEditingFields,
    pagesNumberInputValues,
    setPagesNumberInputValues,
    interiorExteriorMenuVisible,
    setInteriorExteriorMenuVisible,
    dayNightMenuVisible,
    setDayNightMenuVisible,
    interiorExteriorMenuPosition,
    dayNightMenuPosition,
    interiorExteriorButtonRefs,
    dayNightButtonRefs,
    interiorExteriorOptions,
    dayNightOptions,
    cachedScenes,
    setCachedScenes,
    setEditingSceneId,
    getCastRelations,
    getCastRelationsForDisplay,
    formatInteriorExterior,
    formatDayNight,
    validatePagesNumber,
    formatPagesNumber,
    handleInteriorExteriorClick,
    handleDayNightClick,
    handleInteriorExteriorSelect,
    handleDayNightSelect,
    handleFieldUpdate,
    handleAddSlot,
    handleDeleteSlot,
  } = ctx;

  const draggable = draggableProp && ctx.canEdit;
  const editable = editableProp && ctx.canEdit;
  const canDrag = draggable;
  const canModify = editable;

  if (!breakdownID) {
    return <div className="slot-sceneCard not-draggable" />;
  }

  const backgroundColor = getSceneCodeCellBackgroundColor(scene.sceneInteriorExterior, scene.sceneDayNight);

  const isDuplicate = todaySceneIds.has(scene.sceneID);

  const shouldShowAsScheduled =
    variant === 'breakdownTab' &&
    ((scenesListTabFilter === 'all' && scheduledSceneIds.has(scene.sceneID)) ||
      scenesListTabFilter === 'scheduled');

  return (
    <div
      className={`slot-sceneCard ${!canDrag ? 'not-draggable' : ''} ${shouldShowAsScheduled ? 'scheduled' : ''}`}
    >
      <DraggableSceneHeader scene={scene} scheduleID={scheduleID} draggable={canDrag}>
        <div
          className={`slot-sceneCard-header ${shouldShowAsScheduled ? 'scheduled' : ''}`}
          style={{ '--dynamic-bg-color': backgroundColor } as React.CSSProperties}
        >
          <div
            className="slot-sceneCard-header-left"
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
          >
            <div className="slot-sceneCard-header-buttons-top">
              {canModify && variant === 'breakdownTab' && (
                <button
                  className="slot-sceneCard-header-add-btn"
                  onClick={(e) => handleAddSlot(e, scene.sceneID)}
                  disabled={isDuplicate}
                  title={isDuplicate ? t('schedule.alreadyScheduled') : t('schedule.addToThisDay')}
                >
                  <AddIcon className="slot-sceneCard-header-add-icon" />
                </button>
              )}
              {canModify && variant === 'todayTab' && (
                <button
                  className="slot-sceneCard-header-delete"
                  onClick={(e) => handleDeleteSlot(e, scene.sceneID)}
                  title={t('common.delete')}
                >
                  <DeleteIcon className="dayInfo-slot-card-delete-icon" aria-label={t('schedule.remove')} />
                </button>
              )}
            </div>
            {canModify && <div className="slot-sceneCard-header-buttons-bottom">
              {canEdit && <button
                className="slot-sceneCard-header-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingSceneId(scene.sceneID);
                }}
              >
                <EditIcon />
              </button>}
            </div>}
          </div>
          <div className="slot-sceneCard-header-right">
            <div className="slot-sceneCard-header-code slot-sceneCard-header-code-display-right">
              {scene.sceneCode || ''}
            </div>
          </div>
        </div>
      </DraggableSceneHeader>

      <div className="slot-sceneCard-column slot-sceneCard-column-first">
        <div className="slot-sceneCard-column-row">
          {editable ? (
            <>
              <div
                ref={(el) => {
                  interiorExteriorButtonRefs.current[scene.sceneID] = el;
                }}
                className="slot-sceneCard-field slot-sceneCard-field-select-button"
                data-field="sceneInteriorExterior"
                onClick={(e) => handleInteriorExteriorClick(e, scene.sceneID, !editable)}
              >
                {formatInteriorExterior(scene.sceneInteriorExterior) || ''}
              </div>
              {interiorExteriorMenuVisible[scene.sceneID] && interiorExteriorMenuPosition[scene.sceneID] && (
                <DropdownMenu
                  items={interiorExteriorOptions}
                  visible={true}
                  onClose={() =>
                    setInteriorExteriorMenuVisible((prev) => ({ ...prev, [scene.sceneID]: false }))
                  }
                  onSelect={(item) =>
                    handleInteriorExteriorSelect(scene.sceneID, item, breakdownID, cachedScenes, setCachedScenes)
                  }
                  position={{
                    top: interiorExteriorMenuPosition[scene.sceneID].top,
                    left: interiorExteriorMenuPosition[scene.sceneID].left,
                  }}
                  useFixedPosition={true}
                  selectedValue={scene.sceneInteriorExterior}
                  width={interiorExteriorMenuPosition[scene.sceneID].width}
                />
              )}
            </>
          ) : (
            <div className="slot-sceneCard-field">{formatInteriorExterior(scene.sceneInteriorExterior)}</div>
          )}
          {editable ? (
            <>
              <div
                ref={(el) => {
                  dayNightButtonRefs.current[scene.sceneID] = el;
                }}
                className="slot-sceneCard-field slot-sceneCard-field-select-button"
                data-field="sceneDayNight"
                onClick={(e) => handleDayNightClick(e, scene.sceneID, !editable)}
              >
                {formatDayNight(scene.sceneDayNight) || ''}
              </div>
              {dayNightMenuVisible[scene.sceneID] && dayNightMenuPosition[scene.sceneID] && (
                <DropdownMenu
                  items={dayNightOptions}
                  visible={true}
                  onClose={() => setDayNightMenuVisible((prev) => ({ ...prev, [scene.sceneID]: false }))}
                  onSelect={(item) =>
                    handleDayNightSelect(scene.sceneID, item, breakdownID, cachedScenes, setCachedScenes)
                  }
                  position={{
                    top: dayNightMenuPosition[scene.sceneID].top,
                    left: dayNightMenuPosition[scene.sceneID].left,
                  }}
                  useFixedPosition={true}
                  selectedValue={scene.sceneDayNight}
                  width={dayNightMenuPosition[scene.sceneID].width}
                />
              )}
            </>
          ) : (
            <div className="slot-sceneCard-field">{formatDayNight(scene.sceneDayNight)}</div>
          )}
        </div>
        <div className="slot-sceneCard-column-row">
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input slot-sceneCard-input-plotDay"
              value={
                editingFields[scene.sceneID]?.scenePlotDay !== undefined
                  ? editingFields[scene.sceneID].scenePlotDay
                  : scene.scenePlotDay || ''
              }
              onChange={(e) => {
                setEditingFields((prev) => ({
                  ...prev,
                  [scene.sceneID]: {
                    ...prev[scene.sceneID],
                    scenePlotDay: e.target.value,
                  },
                }));
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                handleFieldUpdate(scene.sceneID, 'scenePlotDay', value || undefined, breakdownID, cachedScenes, setCachedScenes);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field">{scene.scenePlotDay || ''}</div>
          )}
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input slot-sceneCard-input-pagesNumber"
              value={
                pagesNumberInputValues[scene.sceneID] !== undefined
                  ? pagesNumberInputValues[scene.sceneID]
                  : scene.scenePagesNumber !== null && scene.scenePagesNumber !== undefined
                    ? String(scene.scenePagesNumber)
                    : ''
              }
              onChange={(e) => {
                const value = e.target.value;
                if (validatePagesNumber(value)) {
                  setPagesNumberInputValues((prev) => ({
                    ...prev,
                    [scene.sceneID]: value,
                  }));
                }
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                const formattedValue = formatPagesNumber(value);
                setPagesNumberInputValues((prev) => {
                  const next = { ...prev };
                  delete next[scene.sceneID];
                  return next;
                });
                handleFieldUpdate(
                  scene.sceneID,
                  'scenePagesNumber',
                  formattedValue,
                  breakdownID,
                  cachedScenes,
                  setCachedScenes,
                );
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field">{scene.scenePagesNumber || ''}</div>
          )}
        </div>
      </div>

      <div className="slot-sceneCard-column slot-sceneCard-column-set-plot">
        <div className="slot-sceneCard-column-row">
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input"
              value={
                editingFields[scene.sceneID]?.sceneSet !== undefined
                  ? editingFields[scene.sceneID].sceneSet
                  : scene.sceneSet || ''
              }
              onChange={(e) => {
                setEditingFields((prev) => ({
                  ...prev,
                  [scene.sceneID]: {
                    ...prev[scene.sceneID],
                    sceneSet: e.target.value,
                  },
                }));
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                handleFieldUpdate(scene.sceneID, 'sceneSet', value || undefined, breakdownID, cachedScenes, setCachedScenes);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field">{scene.sceneSet || ''}</div>
          )}
        </div>
        <div className="slot-sceneCard-column-row">
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input"
              value={
                editingFields[scene.sceneID]?.scenePlot !== undefined
                  ? editingFields[scene.sceneID].scenePlot
                  : scene.scenePlot || ''
              }
              onChange={(e) => {
                setEditingFields((prev) => ({
                  ...prev,
                  [scene.sceneID]: {
                    ...prev[scene.sceneID],
                    scenePlot: e.target.value,
                  },
                }));
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                handleFieldUpdate(scene.sceneID, 'scenePlot', value || undefined, breakdownID, cachedScenes, setCachedScenes);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field">{scene.scenePlot || ''}</div>
          )}
        </div>
      </div>

      <div className="slot-sceneCard-column slot-sceneCard-column-location-cast">
        <div className="slot-sceneCard-column-row slot-sceneCard-column-location-day">
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input slot-sceneCard-field-location"
              value={
                editingFields[scene.sceneID]?.sceneLocation !== undefined
                  ? editingFields[scene.sceneID].sceneLocation
                  : scene.sceneLocation || ''
              }
              onChange={(e) => {
                setEditingFields((prev) => ({
                  ...prev,
                  [scene.sceneID]: {
                    ...prev[scene.sceneID],
                    sceneLocation: e.target.value,
                  },
                }));
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                handleFieldUpdate(scene.sceneID, 'sceneLocation', value || undefined, breakdownID, cachedScenes, setCachedScenes);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field slot-sceneCard-field-location">{scene.sceneLocation || ''}</div>
          )}
        </div>
        <div className="slot-sceneCard-column-row slot-sceneCard-column-cast">
          <div className="slot-sceneCard-field slot-sceneCard-field-cast">
            {(showRestrictedDatesStyle ? getCastRelationsForDisplay(scene.sceneID) : getCastRelations(scene.sceneID)).map(
              (rel, i) => (
                <React.Fragment key={rel.elementID}>
                  {i > 0 && <span className="slot-sceneCard-cast-sep">/</span>}
                  <span
                    className={`slot-sceneCard-cast-item ${
                      showRestrictedDatesStyle && rel.elementID && restrictedDatesCastIdsForToday.has(rel.elementID)
                        ? 'slot-sceneCard-cast-restrictedDates'
                        : ''
                    }`}
                  >
                    {rel.elementAbbr}
                  </span>
                </React.Fragment>
              ),
            )}
          </div>
          {editable ? (
            <input
              type="text"
              className="slot-sceneCard-field slot-sceneCard-input slot-sceneCard-field-period"
              value={
                editingFields[scene.sceneID]?.scenePeriod !== undefined
                  ? editingFields[scene.sceneID].scenePeriod
                  : scene.scenePeriod || ''
              }
              onChange={(e) => {
                setEditingFields((prev) => ({
                  ...prev,
                  [scene.sceneID]: {
                    ...prev[scene.sceneID],
                    scenePeriod: e.target.value,
                  },
                }));
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                handleFieldUpdate(scene.sceneID, 'scenePeriod', value || undefined, breakdownID, cachedScenes, setCachedScenes);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="slot-sceneCard-field slot-sceneCard-field-period">{scene.scenePeriod || ''}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const SlotNoteMultilineInput: React.FC<{
  slot: SlotInfo;
  readOnly: boolean;
  className: string;
  onFinish: (value: string) => void;
  onCancel: () => void;
  onBeginEdit: () => void;
}> = ({ slot, readOnly, className, onFinish, onCancel, onBeginEdit }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const syncHeight = useCallback(() => {
    syncNoteTextareaHeightToContent(ref.current, 20);
  }, []);
  useLayoutEffect(() => {
    syncHeight();
  }, [slot.slotName, slot.slotID, readOnly, syncHeight]);
  useEffect(() => {
    const el = ref.current;
    if (!el || !readOnly) return;
    const next = (slot.slotName || '').replace(/\r\n/g, '\n');
    if (el.value !== next) {
      el.value = next;
      syncHeight();
    }
  }, [slot.slotName, readOnly, syncHeight]);
  return (
    <textarea
      ref={ref}
      rows={1}
      className={className}
      defaultValue={(slot.slotName || '').replace(/\r\n/g, '\n')}
      readOnly={readOnly}
      onInput={(e) => {
        if (!readOnly) syncHeight();
      }}
      onBlur={() => {
        if (!readOnly) onFinish(ref.current?.value.replace(/\r\n/g, '\n') ?? '');
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (readOnly) onBeginEdit();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
};

/** 该日 Tab：与 buildDisplaySegments 一致的场次行 + 便签行 */
export const CurrentDateDetailRowsList: React.FC = () => {
  const { t } = useTranslation();
  const {
    canEdit,
    breakdownID,
    currentDateDetailRows,
    editingNoteSlotID,
    setEditingNoteSlotID,
    handleDeleteNoteSlot,
    handleFinishEditTodayNote,
  } = useScheduleSceneRows();

  if (!breakdownID) {
    return <div className="scenesList-empty">{t('schedule.sceneListEmpty')}</div>;
  }
  if (currentDateDetailRows.length === 0) {
    return <div className="scenesList-empty">{t('schedule.noBreakdown')}</div>;
  }

  return (
    <div className="scenesList-container">
      {currentDateDetailRows.map((row) =>
        row.kind === 'scene' ? (
          <SlotSceneCard
            key={row.scene.sceneID}
            scene={row.scene}
            variant="todayTab"
            scenesListTabFilter="all"
            draggable={false}
            editable
            showRestrictedDatesStyle
          />
        ) : (
          <div key={row.slot.slotID} className="slot-noteCard">
            <div className="slot-noteCard-row dayInfo-slot-bottom-info-note">
              {canEdit && <button
                type="button"
                className="slot-sceneCard-header-delete slot-noteCard-delete"
                onClick={(e) => handleDeleteNoteSlot(e, row.slot.slotID)}
                title={t('common.delete')}
              >
                <DeleteIcon className="dayInfo-slot-card-delete-icon" aria-label={t('schedule.remove')} />
              </button>}
              <div className="dayInfo-slot-bottom-info-note-drag-handle slot-noteCard-iconWrap" aria-hidden>
                <NoteIcon className="dayInfo-slot-bottom-info-note-icon" />
              </div>
              <SlotNoteMultilineInput
                slot={row.slot}
                readOnly={!canEdit || editingNoteSlotID !== row.slot.slotID}
                className={`dayInfo-slot-bottom-info-note-input ${editingNoteSlotID !== row.slot.slotID ? 'dayInfo-slot-bottom-info-note-input-readonly' : ''}`}
                onFinish={(value) => void handleFinishEditTodayNote(row.slot.slotID, value)}
                onCancel={() => setEditingNoteSlotID(null)}
                onBeginEdit={() => {
                  if (canEdit) setEditingNoteSlotID(row.slot.slotID);
                }}
              />
            </div>
          </div>
        ),
      )}
    </div>
  );
};

/** 场次 Tab：可拖拽、可编辑的场次卡片列表 */
export const SceneCardsList: React.FC = () => {
  const { t } = useTranslation();
  const { breakdownID, sortedScenes, effectiveScenesListTab } = useScheduleSceneRows();

  if (!breakdownID) {
    return <div className="scenesList-empty">{t('schedule.sceneListEmpty')}</div>;
  }
  if (sortedScenes.length === 0) {
    return <div className="scenesList-empty">{t('schedule.noBreakdown')}</div>;
  }

  return (
    <div className="scenesList-container">
      {sortedScenes.map((scene) => (
        <SlotSceneCard
          key={scene.sceneID}
          scene={scene}
          variant="breakdownTab"
          scenesListTabFilter={effectiveScenesListTab}
          draggable
          editable
          showRestrictedDatesStyle={false}
        />
      ))}
    </div>
  );
};

export interface ScheduleSceneRowsProviderProps {
  canEdit?: boolean;
  breakdownID?: string;
  elementRelations?: ElementSceneInfo[];
  scheduleID?: string;
  slots: SlotInfo[];
  calDates: CalDateInfo[];
  currentDate: Date;
  onSlotDelete?: (slotID: string) => void;
  onScheduleRowsRefresh?: () => void;
  effectiveScenesListTab: ScenesListTabType;
  effectiveSortKey: ScheduleSceneRowsSortKey;
  normalizeDatesIfNeeded?: (dateStrs: string[]) => Promise<void>;
  children: React.ReactNode;
}

export const ScheduleSceneRowsProvider: React.FC<ScheduleSceneRowsProviderProps> = ({
  canEdit = false,
  breakdownID,
  elementRelations: propsElementRelations = [],
  scheduleID,
  slots,
  calDates,
  currentDate,
  onSlotDelete,
  onScheduleRowsRefresh,
  effectiveScenesListTab,
  effectiveSortKey,
  normalizeDatesIfNeeded,
  children,
}) => {
  const { t } = useTranslation();
  const sortDirection: 'asc' = 'asc';

  const {
    getBreakdownCache,
    setBreakdownCache,
    subscribeBreakdownCache,
    getElementRelationCache,
    subscribeElementRelationCache,
    getElementCache,
    subscribeElementCache,
  } = useDataCache();

  const { getRestrictedDatesCastIdsForDate } = useRestrictedDatesCast(breakdownID ?? undefined);
  const currentDateStr = useMemo(() => formatDateFromDate(currentDate), [currentDate]);
  const restrictedDatesCastIdsForToday = useMemo(
    () => getRestrictedDatesCastIdsForDate(currentDateStr),
    [getRestrictedDatesCastIdsForDate, currentDateStr],
  );

  const { updateSceneData } = useSceneUpdates();
  const { createSlotData, deleteSlotData, updateSlotData } = useSlotUpdates();
  const { upsertCalDateData } = useCalDateUpdates();
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [editingNoteSlotID, setEditingNoteSlotID] = useState<string | null>(null);
  const [enrichedRelations, setEnrichedRelations] = useState<ElementSceneInfo[]>(propsElementRelations || []);

  const [editingFields, setEditingFields] = useState<{ [sceneID: string]: { [field: string]: string } }>({});
  const [pagesNumberInputValues, setPagesNumberInputValues] = useState<{ [sceneID: string]: string | undefined }>({});
  const updateTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const [interiorExteriorMenuVisible, setInteriorExteriorMenuVisible] = useState<{ [sceneID: string]: boolean }>({});
  const [dayNightMenuVisible, setDayNightMenuVisible] = useState<{ [sceneID: string]: boolean }>({});
  const [interiorExteriorMenuPosition, setInteriorExteriorMenuPosition] = useState<{
    [sceneID: string]: { top: number; left: number; width: number };
  }>({});
  const [dayNightMenuPosition, setDayNightMenuPosition] = useState<{
    [sceneID: string]: { top: number; left: number; width: number };
  }>({});
  const interiorExteriorButtonRefs = useRef<{ [sceneID: string]: HTMLDivElement | null }>({});
  const dayNightButtonRefs = useRef<{ [sceneID: string]: HTMLDivElement | null }>({});

  const interiorExteriorOptions: DropdownMenuItem[] = [
    { id: SceneInteriorExterior.INTERIOR, label: t('sceneDetails.ioInterior') },
    { id: SceneInteriorExterior.EXTERIOR, label: t('sceneDetails.ioExterior') },
    { id: SceneInteriorExterior.INTERIOR_EXTERIOR, label: t('sceneDetails.ioInteriorExterior') },
  ];

  const dayNightOptions: DropdownMenuItem[] = [
    { id: SceneDayNight.MORNING, label: t('sceneDetails.dnMorning') },
    { id: SceneDayNight.DAY, label: t('sceneDetails.dnDay') },
    { id: SceneDayNight.DUSK, label: t('sceneDetails.dnDusk') },
    { id: SceneDayNight.NIGHT, label: t('sceneDetails.dnNight') },
  ];

  const scheduledSceneIds = useMemo(() => {
    const sceneIdSet = new Set<string>();
    slots.forEach((slot) => {
      if (slot.slotBelongSceneID) {
        sceneIdSet.add(slot.slotBelongSceneID);
      }
    });
    return sceneIdSet;
  }, [slots]);

  const todaySceneIds = useMemo(() => {
    if (!currentDate) return new Set<string>();
    const dStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    const sceneIdSet = new Set<string>();
    slots.forEach((slot) => {
      if (slot.slotDate && slot.slotBelongSceneID) {
        const slotDateStr = slot.slotDate.split('T')[0];
        if (slotDateStr === dStr) {
          sceneIdSet.add(slot.slotBelongSceneID);
        }
      }
    });
    return sceneIdSet;
  }, [slots, currentDate]);

  const handleInteriorExteriorClick = (e: React.MouseEvent, sceneID: string, _draggable: boolean) => {
    const button = interiorExteriorButtonRefs.current[sceneID];
    if (button) {
      const rect = button.getBoundingClientRect();
      setInteriorExteriorMenuPosition((prev) => ({
        ...prev,
        [sceneID]: { top: rect.bottom + 4, left: rect.left, width: rect.width },
      }));
    }
    setInteriorExteriorMenuVisible((prev) => ({ ...prev, [sceneID]: !prev[sceneID] }));
  };

  const handleDayNightClick = (e: React.MouseEvent, sceneID: string, _draggable: boolean) => {
    const button = dayNightButtonRefs.current[sceneID];
    if (button) {
      const rect = button.getBoundingClientRect();
      setDayNightMenuPosition((prev) => ({
        ...prev,
        [sceneID]: { top: rect.bottom + 4, left: rect.left, width: rect.width },
      }));
    }
    setDayNightMenuVisible((prev) => ({ ...prev, [sceneID]: !prev[sceneID] }));
  };

  const validatePagesNumber = (input: string): boolean => {
    if (input === '') return true;
    const normalizedInput = input.replace(/。/g, '.');
    if (!/^[\d.]*$/.test(normalizedInput)) return false;
    const dotCount = (normalizedInput.match(/\./g) || []).length;
    if (dotCount > 1) return false;
    const parts = normalizedInput.split('.');
    if (parts.length === 2 && parts[1].length > 1) return false;
    const integerPart = parts[0];
    if (integerPart.length > 2) return false;
    if (!normalizedInput.endsWith('.') && normalizedInput !== '') {
      const numValue = parseFloat(normalizedInput);
      if (!isNaN(numValue) && (numValue < 0 || numValue > 99.9)) {
        return false;
      }
    }
    return true;
  };

  const formatPagesNumber = (input: string): number | undefined => {
    const trimmed = input.trim();
    if (trimmed === '') return undefined;
    const numValue = parseFloat(trimmed);
    if (isNaN(numValue)) return undefined;
    if (numValue < 0 || numValue > 99.9) return undefined;
    const decimalParts = trimmed.split('.');
    if (decimalParts.length === 2 && decimalParts[1].length > 1) return undefined;
    const rounded = Math.round(numValue * 10) / 10;
    if (rounded < 0 || rounded > 99.9) return undefined;
    return rounded;
  };

  const handleFieldUpdate = async (
    sceneID: string,
    field: keyof UpdateSceneParams,
    value: any,
    sid: string,
    _cachedScenes: SceneWithUserNames[],
    _setCachedScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => {
    const currentCache = getBreakdownCache(sid);
    if (currentCache) {
      const updatedScenes = currentCache.scenes.map((scene) =>
        scene.sceneID === sceneID ? { ...scene, [field]: value } : scene,
      );
      let updatedSceneLocationDisplayTexts = currentCache.sceneLocationDisplayTexts || {};
      let updatedSceneSetDisplayTexts = currentCache.sceneSetDisplayTexts || {};
      if (field === 'sceneLocation') {
        updatedSceneLocationDisplayTexts = { ...updatedSceneLocationDisplayTexts, [sceneID]: value || '' };
      }
      if (field === 'sceneSet') {
        updatedSceneSetDisplayTexts = { ...updatedSceneSetDisplayTexts, [sceneID]: value || '' };
      }
      setBreakdownCache(sid, {
        scenes: updatedScenes,
        sceneLocationDisplayTexts: updatedSceneLocationDisplayTexts,
        sceneSetDisplayTexts: updatedSceneSetDisplayTexts,
      });
    }

    const existingTimer = updateTimersRef.current.get(sceneID);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(async () => {
      try {
        await updateSceneData(sceneID, { [field]: value }, sid);
        setEditingFields((prev) => {
          const next = { ...prev };
          if (next[sceneID]) {
            delete next[sceneID][field];
            if (Object.keys(next[sceneID]).length === 0) {
              delete next[sceneID];
            }
          }
          return next;
        });
        if (field === 'scenePagesNumber') {
          setPagesNumberInputValues((prev) => {
            const next = { ...prev };
            delete next[sceneID];
            return next;
          });
        }
      } catch (error) {
        console.error(`更新场次 ${field} 失败:`, error);
        alert(`${t('schedule.updateFailed')}${error instanceof Error ? error.message : t('common.unknownError')}`);
        setEditingFields((prev) => {
          const next = { ...prev };
          if (next[sceneID]) {
            delete next[sceneID][field];
            if (Object.keys(next[sceneID]).length === 0) {
              delete next[sceneID];
            }
          }
          return next;
        });
        if (field === 'scenePagesNumber') {
          setPagesNumberInputValues((prev) => {
            const next = { ...prev };
            delete next[sceneID];
            return next;
          });
        }
      } finally {
        updateTimersRef.current.delete(sceneID);
      }
    }, 500);

    updateTimersRef.current.set(sceneID, timer);
  };

  const [cachedScenes, setCachedScenes] = useState<SceneWithUserNames[]>([]);

  useEffect(() => {
    if (!breakdownID) {
      setCachedScenes([]);
      return;
    }
    const subscribeCachedScenesFromCache = () => {
      const cachedData = getBreakdownCache(breakdownID);
      if (cachedData?.scenes && cachedData.scenes.length > 0) {
        setCachedScenes(cachedData.scenes);
      } else {
        setCachedScenes([]);
      }
    };
    subscribeCachedScenesFromCache();
    return subscribeBreakdownCache(breakdownID, subscribeCachedScenesFromCache);
  }, [breakdownID, getBreakdownCache, subscribeBreakdownCache]);

  const cachedData = useMemo(() => {
    if (!breakdownID) return null;
    const data = getBreakdownCache(breakdownID);
    return data ? { ...data, scenes: cachedScenes.length > 0 ? cachedScenes : data.scenes } : null;
  }, [breakdownID, getBreakdownCache, cachedScenes]);

  useEffect(() => {
    if (!breakdownID) {
      setEnrichedRelations(propsElementRelations || []);
      return;
    }
    const updateRelations = () => {
      const relationCache = getElementRelationCache(breakdownID);
      const itemCache = getElementCache(breakdownID);
      if (relationCache?.elementRelations && itemCache?.elements) {
        const enriched = enrichElementRelations(relationCache.elementRelations, itemCache.elements);
        setEnrichedRelations(enriched);
      } else if (propsElementRelations && propsElementRelations.length > 0) {
        setEnrichedRelations(propsElementRelations);
      } else {
        setEnrichedRelations([]);
      }
    };
    updateRelations();
    const unsubscribeRelation = subscribeElementRelationCache(breakdownID, updateRelations);
    const unsubscribeItem = subscribeElementCache(breakdownID, updateRelations);
    return () => {
      unsubscribeRelation();
      unsubscribeItem();
    };
  }, [
    breakdownID,
    propsElementRelations,
    getElementRelationCache,
    getElementCache,
    subscribeElementRelationCache,
    subscribeElementCache,
  ]);

  const elementRelations = enrichedRelations;

  const isSceneRowEmpty = (scene: SceneWithUserNames): boolean => {
    const hasSceneCode = !!scene.sceneCode?.trim();
    const hasInteriorExterior = !!scene.sceneInteriorExterior;
    const hasDayNight = !!scene.sceneDayNight;
    const hasSceneSet = !!scene.sceneSet?.trim();
    const hasScenePlotDay = !!scene.scenePlotDay?.trim();
    const hasScenePagesNumber = !!scene.scenePagesNumber;
    const hasScenePlot = !!scene.scenePlot?.trim();
    const hasSceneLocation = !!scene.sceneLocation?.trim();
    return (
      !hasSceneCode &&
      !hasInteriorExterior &&
      !hasDayNight &&
      !hasSceneSet &&
      !hasScenePlotDay &&
      !hasScenePagesNumber &&
      !hasScenePlot &&
      !hasSceneLocation
    );
  };

  const nonEmptyScenes: SceneWithUserNames[] = useMemo(() => {
    const scenes = cachedData?.scenes || [];
    return scenes.filter((scene) => !isSceneRowEmpty(scene));
  }, [cachedData]);

  const filteredScenesForScenesSubtab: SceneWithUserNames[] = useMemo(() => {
    if (effectiveScenesListTab === 'all') {
      return nonEmptyScenes;
    }
    if (effectiveScenesListTab === 'scheduled') {
      return nonEmptyScenes.filter((scene) => scheduledSceneIds.has(scene.sceneID));
    }
    if (effectiveScenesListTab === 'unscheduled') {
      return nonEmptyScenes.filter((scene) => !scheduledSceneIds.has(scene.sceneID));
    }
    return nonEmptyScenes;
  }, [nonEmptyScenes, effectiveScenesListTab, scheduledSceneIds]);

  const sortedScenes: SceneWithUserNames[] = useMemo(() => {
    return sortScenes(filteredScenesForScenesSubtab, effectiveSortKey, sortDirection);
  }, [filteredScenesForScenesSubtab, effectiveSortKey, sortDirection]);

  const filteredScenesForTodayTab: SceneWithUserNames[] = useMemo(() => {
    return nonEmptyScenes.filter((scene) => todaySceneIds.has(scene.sceneID));
  }, [nonEmptyScenes, todaySceneIds]);

  const slotsForCurrentDate: SlotInfo[] = useMemo(
    () => slots.filter((slot) => slot.slotDate?.split('T')[0] === currentDateStr),
    [slots, currentDateStr],
  );

  const todayCalDateEntry = useMemo(
    () => calDates.find((pd) => pd.calDateDt?.split('T')[0] === currentDateStr),
    [calDates, currentDateStr],
  );

  const currentDateDetailRows: CurrentDateDetailRow[] = useMemo(() => {
    const sceneMap = new Map(filteredScenesForTodayTab.map((s) => [s.sceneID, s]));
    const slotMap = new Map(slotsForCurrentDate.map((t) => [t.slotID, t]));
    const rows: CurrentDateDetailRow[] = [];
    const usedSceneIds = new Set<string>();
    const usedNoteIds = new Set<string>();

    const segments = buildDisplaySegments(
      slotsForCurrentDate,
      todayCalDateEntry?.calDateSlotsSort,
      currentDateStr,
      (sceneID) => {
        const s = nonEmptyScenes.find((sc) => sc.sceneID === sceneID);
        return s ? { sceneLocation: s.sceneLocation } : undefined;
      },
    );

    for (const seg of segments) {
      if (seg.kind === 'note') {
        for (const id of seg.items) {
          const slot = slotMap.get(id);
          if (slot && !usedNoteIds.has(id)) {
            rows.push({ kind: 'note', slot: slot });
            usedNoteIds.add(id);
          }
        }
      } else {
        for (const id of seg.items) {
          const slot = slotMap.get(id);
          if (!slot?.slotBelongSceneID) continue;
          const sid = slot.slotBelongSceneID;
          if (!sceneMap.has(sid) || usedSceneIds.has(sid)) continue;
          rows.push({ kind: 'scene', scene: sceneMap.get(sid)! });
          usedSceneIds.add(sid);
        }
      }
    }

    const remainingScenes = filteredScenesForTodayTab.filter((s) => !usedSceneIds.has(s.sceneID));
    rows.push(
      ...sortScenes(remainingScenes, effectiveSortKey, sortDirection).map((s) => ({
        kind: 'scene' as const,
        scene: s,
      })),
    );

    for (const slot of slotsForCurrentDate) {
      const isNote = slot.slotType === SlotType.NOTE || !slot.slotType;
      if (isNote && !usedNoteIds.has(slot.slotID)) {
        rows.push({ kind: 'note', slot: slot });
        usedNoteIds.add(slot.slotID);
      }
    }

    return rows;
  }, [
    filteredScenesForTodayTab,
    slotsForCurrentDate,
    todayCalDateEntry,
    currentDateStr,
    nonEmptyScenes,
    effectiveSortKey,
    sortDirection,
  ]);

  const getCastRelations = useCallback(
    (sceneID: string): ElementSceneInfo[] => {
      const castRelations = elementRelations.filter(
        (rel) => rel.sceneID === sceneID && rel.elementType === ElementType.CAST && rel.elementAbbr,
      );
      return sortCastItemsByNO(castRelations);
    },
    [elementRelations],
  );

  const getCastRelationsForDisplay = useCallback(
    (sceneID: string): ElementSceneInfo[] => {
      const all = getCastRelations(sceneID);
      const restrictedDatesFirst: ElementSceneInfo[] = [];
      const nonRestrictedDates: ElementSceneInfo[] = [];
      for (const rel of all) {
        if (rel.elementID && restrictedDatesCastIdsForToday.has(rel.elementID)) {
          restrictedDatesFirst.push(rel);
        } else {
          nonRestrictedDates.push(rel);
        }
      }
      return [...restrictedDatesFirst, ...nonRestrictedDates];
    },
    [getCastRelations, restrictedDatesCastIdsForToday],
  );

  const formatInteriorExterior = useCallback((value?: SceneInteriorExterior): string => {
    if (!value) return '';
    const map: Record<SceneInteriorExterior, string> = {
      [SceneInteriorExterior.INTERIOR]: t('sceneDetails.ioInterior'),
      [SceneInteriorExterior.EXTERIOR]: t('sceneDetails.ioExterior'),
      [SceneInteriorExterior.INTERIOR_EXTERIOR]: t('sceneDetails.ioInteriorExterior'),
    };
    return map[value] || '';
  }, [t]);

  const formatDayNight = useCallback((value?: SceneDayNight): string => {
    if (!value) return '';
    const map: Record<SceneDayNight, string> = {
      [SceneDayNight.MORNING]: t('sceneDetails.dnMorning'),
      [SceneDayNight.DAY]: t('sceneDetails.dnDay'),
      [SceneDayNight.DUSK]: t('sceneDetails.dnDusk'),
      [SceneDayNight.NIGHT]: t('sceneDetails.dnNight'),
    };
    return map[value] || '';
  }, [t]);

  const updateCalDateSlotsSort = async (dateStr: string, additionalSoltIds?: string[]) => {
    if (!scheduleID) return;
    const existingCalDate = calDates.find((pd) => {
      if (!pd.calDateDt) return false;
      return pd.calDateDt.split('T')[0] === dateStr;
    });

    if (additionalSoltIds?.length) {
      const newFlat = mergeSortIds(
        normalizeToSortedIds(existingCalDate?.calDateSlotsSort),
        additionalSoltIds,
      );
      await upsertCalDateData(scheduleID, {
        calDateDt: dateStr,
        calDateBelongScheduleID: scheduleID,
        calDateSlotsSort: newFlat,
      });
      return;
    }

    const dateSlots = slots.filter((slot) => {
      if (!slot.slotDate) return false;
      return slot.slotDate.split('T')[0] === dateStr;
    });

    if (dateSlots.length === 0) {
      if (existingCalDate?.calDateSlotsSort?.length) {
        await upsertCalDateData(scheduleID, {
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: [],
        });
      }
      return;
    }

    const base = normalizeToSortedIds(existingCalDate?.calDateSlotsSort);
    const idsOnDate = new Set(dateSlots.map((slot) => slot.slotID));
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const id of base) {
      if (!idsOnDate.has(id) || seen.has(id)) continue;
      ordered.push(id);
      seen.add(id);
    }
    for (const slot of dateSlots) {
      if (seen.has(slot.slotID)) continue;
      ordered.push(slot.slotID);
      seen.add(slot.slotID);
    }

    await upsertCalDateData(scheduleID, {
      calDateDt: dateStr,
      calDateBelongScheduleID: scheduleID,
      calDateSlotsSort: ordered,
    });
  };

  const handleInteriorExteriorSelect = (
    sceneID: string,
    item: DropdownMenuItem,
    sid: string,
    cs: SceneWithUserNames[],
    setCs: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => {
    const value = item.id as SceneInteriorExterior;
    void handleFieldUpdate(sceneID, 'sceneInteriorExterior', value, sid, cs, setCs);
    setInteriorExteriorMenuVisible((prev) => ({ ...prev, [sceneID]: false }));
  };

  const handleDayNightSelect = (
    sceneID: string,
    item: DropdownMenuItem,
    sid: string,
    cs: SceneWithUserNames[],
    setCs: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  ) => {
    const value = item.id as SceneDayNight;
    void handleFieldUpdate(sceneID, 'sceneDayNight', value, sid, cs, setCs);
    setDayNightMenuVisible((prev) => ({ ...prev, [sceneID]: false }));
  };

  const handleAddSlot = async (e: React.MouseEvent, sceneID: string) => {
    e.stopPropagation();
    if (!scheduleID || !currentDate) return;
    if (todaySceneIds.has(sceneID)) {
      console.warn('该场次已在该日拍摄计划中');
      return;
    }
    try {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const scene = nonEmptyScenes.find((s) => s.sceneID === sceneID);
      if (!scene) {
        console.error('未找到场景信息');
        return;
      }
      const newSlotID = await createSlotData(scheduleID, {
        slotType: SlotType.SHOOT,
        slotName: scene.scenePlotSummary,
        slotSceneCode: scene.sceneCode,
        slotBelongSceneID: scene.sceneID,
        slotBelongScheduleID: scheduleID,
        slotBelongBreakdownID: scene.sceneBelongBreakdownID,
        slotLocation: scene.sceneLocation,
        slotStatus: SlotStatus.PREPARING,
        slotDate: dateStr,
        slotEndDay: dateStr,
      });
      if (newSlotID) {
        await updateCalDateSlotsSort(dateStr, [newSlotID]);
      }
      if (onScheduleRowsRefresh) {
        await onScheduleRowsRefresh();
      }
    } catch (error) {
      console.error('创建拍摄任务失败:', error);
      alert(t('schedule.createFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const handleDeleteSlot = async (e: React.MouseEvent, sceneID: string) => {
    e.stopPropagation();
    if (!scheduleID || !currentDate) return;
    try {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const slot = slots.find(
        (slot) => slot.slotBelongSceneID === sceneID && slot.slotDate?.split('T')[0] === dateStr,
      );
      if (!slot || !slot.slotID) {
        console.warn('未找到对应的拍摄任务', { sceneID, dateStr });
        return;
      }
      const slotID = slot.slotID;
      const slotDateStr = slot.slotDate?.split('T')[0];
      await deleteSlotData(scheduleID, slotID);
      if (slotDateStr && scheduleID) {
        const todayCalDate = calDates?.find((pd) => pd.calDateDt?.split('T')[0] === slotDateStr);
        const newFlat = normalizeToSortedIds(todayCalDate?.calDateSlotsSort).filter((id) => id !== slotID);
        await upsertCalDateData(scheduleID, {
          calDateDt: slotDateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: newFlat,
        });
      }
      onSlotDelete?.(slotID);
    } catch (error) {
      console.error('删除拍摄任务失败:', error);
      alert(t('schedule.deleteFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const handleDeleteNoteSlot = async (e: React.MouseEvent, slotID: string) => {
    e.stopPropagation();
    if (!scheduleID) return;
    try {
      const slot = slots.find((slot) => slot.slotID === slotID);
      const slotDateStr = slot?.slotDate?.split('T')[0];
      await deleteSlotData(scheduleID, slotID);
      if (slotDateStr) {
        const pd = calDates?.find((p) => p.calDateDt?.split('T')[0] === slotDateStr);
        const newFlat = normalizeToSortedIds(pd?.calDateSlotsSort).filter((id) => id !== slotID);
        await upsertCalDateData(scheduleID, {
          calDateDt: slotDateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: newFlat,
        });
      }
      setEditingNoteSlotID((cur) => (cur === slotID ? null : cur));
      onSlotDelete?.(slotID);
    } catch (error) {
      console.error('删除便签失败:', error);
      alert(t('schedule.deleteFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const handleFinishEditTodayNote = async (slotID: string, content: string) => {
    if (!scheduleID) return;
    const slot = slots.find((slot) => slot.slotID === slotID);
    const trimmed = content.trim();
    if (trimmed === (slot?.slotName || '')) {
      setEditingNoteSlotID(null);
      return;
    }
    try {
      await updateSlotData(scheduleID, slotID, { slotName: trimmed });
      setEditingNoteSlotID(null);
      await onScheduleRowsRefresh?.();
    } catch (error) {
      console.error('更新便签失败:', error);
      alert(t('schedule.updateFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const contextValue: ScheduleSceneRowsContextValue = {
    canEdit,
    breakdownID,
    scheduleID,
    sortedScenes,
    elementRelations,
    todaySceneIds,
    restrictedDatesCastIdsForToday,
    currentDateDetailRows,
    scheduledSceneIds,
    effectiveScenesListTab,
    editingFields,
    setEditingFields,
    pagesNumberInputValues,
    setPagesNumberInputValues,
    interiorExteriorMenuVisible,
    setInteriorExteriorMenuVisible,
    dayNightMenuVisible,
    setDayNightMenuVisible,
    interiorExteriorMenuPosition,
    setInteriorExteriorMenuPosition,
    dayNightMenuPosition,
    setDayNightMenuPosition,
    interiorExteriorButtonRefs,
    dayNightButtonRefs,
    interiorExteriorOptions,
    dayNightOptions,
    cachedScenes,
    setCachedScenes,
    editingNoteSlotID,
    setEditingNoteSlotID,
    setEditingSceneId,
    getCastRelations,
    getCastRelationsForDisplay,
    formatInteriorExterior,
    formatDayNight,
    validatePagesNumber,
    formatPagesNumber,
    handleInteriorExteriorClick,
    handleDayNightClick,
    handleInteriorExteriorSelect,
    handleDayNightSelect,
    handleFieldUpdate,
    handleAddSlot,
    handleDeleteSlot,
    handleDeleteNoteSlot,
    handleFinishEditTodayNote,
  };

  return (
    <ScheduleSceneRowsContext.Provider value={contextValue}>
      {children}
      {editingSceneId && (
        <EditScene
          visible={!!editingSceneId}
          onClose={() => setEditingSceneId(null)}
          sceneId={editingSceneId}
          breakdownID={breakdownID || ''}
          canEdit={canEdit}
          drawerTop={EDIT_SCENE_PANEL_DRAWER_TOP_PX}
        />
      )}
    </ScheduleSceneRowsContext.Provider>
  );
};
